import { type FC, useEffect, useState, useRef, useCallback } from 'react';
import commentsApi from '@/api/comments.api';
import workspacesApi from '@/api/workspaces.api';
import type { Comment } from '@/types/comment.types';
import Button from '@/components/ui/Button';

interface CommentsPanelProps {
  nodeId: string | null;
  workspaceId: string;
  onClose: () => void;
}

interface MentionSuggestion {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}

const formatTimestamp = (iso: string): string => {
  const date = new Date(iso);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CommentItem: FC<{
  comment: Comment;
  replies: Comment[];
  onReply: (parentId: string) => void;
}> = ({ comment, replies, onReply }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--spacing-xs)',
    }}
  >
    <div
      style={{
        padding: 'var(--spacing-sm) var(--spacing-md)',
        backgroundColor: 'var(--color-background)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            color: 'var(--color-text)',
          }}
        >
          {comment.authorDisplayName}
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
          }}
        >
          {formatTimestamp(comment.createdAt)}
        </span>
      </div>
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text)',
          margin: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
        dangerouslySetInnerHTML={{
          __html: comment.content.replace(
            /@(\w+\s\w+)/g,
            '<strong style="color: var(--color-primary)">@$1</strong>',
          ),
        }}
      />
      <button
        onClick={() => onReply(comment.id)}
        style={{
          marginTop: 4,
          padding: 0,
          background: 'none',
          border: 'none',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          cursor: 'pointer',
        }}
      >
        Repondre
      </button>
    </div>

    {/* Replies */}
    {replies.length > 0 && (
      <div
        style={{
          marginLeft: 'var(--spacing-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-xs)',
          borderLeft: '2px solid var(--color-border)',
          paddingLeft: 'var(--spacing-sm)',
        }}
      >
        {replies.map((reply) => (
          <div
            key={reply.id}
            style={{
              padding: 'var(--spacing-xs) var(--spacing-sm)',
              backgroundColor: 'var(--color-background)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  color: 'var(--color-text)',
                }}
              >
                {reply.authorDisplayName}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                }}
              >
                {formatTimestamp(reply.createdAt)}
              </span>
            </div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text)',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
              dangerouslySetInnerHTML={{
                __html: reply.content.replace(
                  /@(\w+\s\w+)/g,
                  '<strong style="color: var(--color-primary)">@$1</strong>',
                ),
              }}
            />
          </div>
        ))}
      </div>
    )}
  </div>
);

export const CommentsPanel: FC<CommentsPanelProps> = ({
  nodeId,
  workspaceId,
  onClose,
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [content, setContent] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [members, setMembers] = useState<MentionSuggestion[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isOpen = nodeId !== null;

  useEffect(() => {
    if (!nodeId) {
      setComments([]);
      return;
    }
    loadComments(nodeId);
  }, [nodeId]);

  useEffect(() => {
    if (workspaceId) {
      workspacesApi.getMembers(workspaceId).then((data) => {
        setMembers(
          data.map((m) => ({
            userId: m.userId,
            firstName: m.firstName,
            lastName: m.lastName,
            email: m.email,
          })),
        );
      }).catch(() => {
        // Silently fail
      });
    }
  }, [workspaceId]);

  const loadComments = async (nId: string) => {
    setIsLoading(true);
    try {
      const data = await commentsApi.listByNode(nId);
      setComments(data);
    } catch {
      // Error handling
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!content.trim() || !nodeId) return;
    setIsSending(true);
    try {
      const newComment = await commentsApi.create({
        content: content.trim(),
        nodeId,
        parentId: replyTo ?? undefined,
      });
      setComments((prev) => [...prev, newComment]);
      setContent('');
      setReplyTo(null);
    } catch {
      // Error handling
    } finally {
      setIsSending(false);
    }
  };

  const handleReply = useCallback((parentId: string) => {
    setReplyTo(parentId);
    textareaRef.current?.focus();
  }, []);

  const handleTextChange = (value: string) => {
    setContent(value);

    // Check for @ mentions
    const cursorPos = textareaRef.current?.selectionStart ?? value.length;
    const textUpToCursor = value.slice(0, cursorPos);
    const atMatch = textUpToCursor.match(/@(\w*)$/);

    if (atMatch) {
      setShowMentions(true);
      setMentionFilter(atMatch[1].toLowerCase());
    } else {
      setShowMentions(false);
      setMentionFilter('');
    }
  };

  const insertMention = (member: MentionSuggestion) => {
    const cursorPos = textareaRef.current?.selectionStart ?? content.length;
    const textUpToCursor = content.slice(0, cursorPos);
    const atIndex = textUpToCursor.lastIndexOf('@');

    if (atIndex === -1) return;

    const before = content.slice(0, atIndex);
    const after = content.slice(cursorPos);
    const mention = `@${member.firstName} ${member.lastName}`;
    const newContent = before + mention + ' ' + after;

    setContent(newContent);
    setShowMentions(false);
    setMentionFilter('');
    textareaRef.current?.focus();
  };

  const filteredMentions = members.filter((m) => {
    if (!mentionFilter) return true;
    const fullName = `${m.firstName} ${m.lastName}`.toLowerCase();
    return (
      fullName.includes(mentionFilter) ||
      m.email.toLowerCase().includes(mentionFilter)
    );
  });

  // Group comments: top-level and their replies
  const topLevel = comments.filter((c) => !c.parentId);
  const repliesMap = new Map<string, Comment[]>();
  comments
    .filter((c) => c.parentId)
    .forEach((c) => {
      const existing = repliesMap.get(c.parentId!) ?? [];
      existing.push(c);
      repliesMap.set(c.parentId!, existing);
    });

  const replyingToComment = replyTo
    ? comments.find((c) => c.id === replyTo)
    : null;

  return (
    <div
      className="comments-panel"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: 380,
        maxWidth: '100vw',
        height: '100vh',
        backgroundColor: 'var(--color-surface)',
        borderLeft: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        flexDirection: 'column',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease-in-out',
        zIndex: 50,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 'var(--spacing-md) var(--spacing-lg)',
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0,
        }}
      >
        <h3
          style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 600,
            color: 'var(--color-text)',
            margin: 0,
          }}
        >
          Commentaires
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 18,
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
            padding: 4,
          }}
          title="Fermer"
        >
          x
        </button>
      </div>

      {/* Comments list */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--spacing-md) var(--spacing-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-md)',
        }}
      >
        {isLoading && (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            Chargement...
          </p>
        )}

        {!isLoading && topLevel.length === 0 && (
          <p
            style={{
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
              textAlign: 'center',
              padding: 'var(--spacing-xl) 0',
            }}
          >
            Aucun commentaire. Soyez le premier !
          </p>
        )}

        {topLevel.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            replies={repliesMap.get(comment.id) ?? []}
            onReply={handleReply}
          />
        ))}
      </div>

      {/* Input area */}
      <div
        style={{
          padding: 'var(--spacing-md) var(--spacing-lg)',
          borderTop: '1px solid var(--color-border)',
          flexShrink: 0,
          position: 'relative',
        }}
      >
        {replyingToComment && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--spacing-xs)',
              padding: '4px 8px',
              backgroundColor: 'var(--color-background)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
            }}
          >
            <span>
              Reponse a {replyingToComment.authorDisplayName}
            </span>
            <button
              onClick={() => setReplyTo(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-text-muted)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-xs)',
              }}
            >
              x
            </button>
          </div>
        )}

        {/* Mention dropdown */}
        {showMentions && filteredMentions.length > 0 && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              left: 'var(--spacing-lg)',
              right: 'var(--spacing-lg)',
              maxHeight: 180,
              overflowY: 'auto',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              zIndex: 60,
            }}
          >
            {filteredMentions.map((member) => (
              <button
                key={member.userId}
                onClick={() => insertMention(member)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  padding: 'var(--spacing-xs) var(--spacing-sm)',
                  background: 'none',
                  border: 'none',
                  borderBottom: '1px solid var(--color-border)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-background)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    color: 'var(--color-text)',
                  }}
                >
                  {member.firstName} {member.lastName}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  {member.email}
                </span>
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'flex-end' }}>
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
              if (e.key === 'Escape') {
                setShowMentions(false);
              }
            }}
            placeholder="Ecrire un commentaire... (@mention)"
            rows={2}
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor: 'var(--color-background)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              outline: 'none',
              resize: 'none',
              fontFamily: 'inherit',
            }}
          />
          <Button
            size="sm"
            onClick={handleSend}
            isLoading={isSending}
            disabled={!content.trim()}
          >
            Envoyer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CommentsPanel;
